import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Gauge, Plus, Search, Filter, SlidersHorizontal, Download } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import {
  DataTable,
  EmptyState,
  StatusPill,
  Stat,
  StatGroup,
  FilterBuilder,
  type StatusTone,
  type ColumnDef,
  type FilterField,
  type FilterCondition,
} from '@vnetwork/ui/data';
import {
  Button,
  Badge,
  Input,
  Skeleton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useKumaMonitors,
  useDeleteKumaMonitor,
  useMonitorStats,
  useMonitorEvents,
  type KumaMonitor,
  type MonitorEvent,
} from '@/api/monitoring/kuma';
import { useKumaMetrics, type KumaMetricsByName } from '@/api/monitoring/prometheus';
import { RowMenu } from '../../components/RowMenu';

const muted = (v: React.ReactNode) => (
  <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{v}</span>
);

// Columns the user can toggle via "Display options" (name + actions are fixed).
const TOGGLEABLE = [
  { id: 'url', label: 'URL' },
  { id: 'type', label: 'Type' },
  { id: 'interval', label: 'Interval' },
  { id: 'status', label: 'Status' },
  { id: 'uptime', label: 'Uptime 24h' },
  { id: 'latency', label: 'Latency' },
];
const DEFAULT_VISIBLE: Record<string, boolean> = { url: true, type: true, interval: true, status: true, uptime: true, latency: true };

// Monitor-type labels mirror @mgmt monitorTypeSpecs (the create-form schema).
const TYPE_LABEL: Record<string, string> = {
  http: 'HTTP(s)',
  keyword: 'HTTP(s) - Keyword',
  'json-query': 'HTTP(s) - JSON Query',
  port: 'TCP Port',
  ping: 'Ping (ICMP)',
  dns: 'DNS',
  'grpc-keyword': 'gRPC',
  push: 'Push (passive)',
};

// Derived health: Kuma `active` flag combined with the live Prometheus sample.
// Paused (inactive) has no series; active-but-unscraped stays neutral "active".
const STATUS_LABEL: Record<string, string> = { up: 'Up', down: 'Down', active: 'Active', paused: 'Paused' };
function monitorStatusKey(m: KumaMonitor, up: boolean | undefined): keyof typeof STATUS_LABEL {
  if (!m.active) return 'paused';
  if (up === true) return 'up';
  if (up === false) return 'down';
  return 'active';
}

/** Download the given monitors as a CSV file (live health joined from Prometheus). */
function exportCsv(rows: KumaMonitor[], metrics: KumaMetricsByName | undefined) {
  const head = ['name', 'url', 'type', 'interval', 'status', 'uptime_24h', 'latency_ms'];
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows
    .map((m) => {
      const mt = metrics?.[m.name];
      return [
        m.name,
        m.url,
        m.type,
        `${m.interval}s`,
        monitorStatusKey(m, mt?.up),
        mt?.uptime24h != null ? mt.uptime24h.toFixed(2) : '',
        mt?.responseMs ?? '',
      ]
        .map(esc)
        .join(',');
    })
    .join('\n');
  const blob = new Blob([`${head.join(',')}\n${body}`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `uptime-monitors-${rows.length}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Uptime-Kuma monitor inventory. Monitors are org-global (not project-scoped) —
 * the page just lives under the project console for navigation.
 *
 * Inventory (name/url/type/interval/active) comes from the Kuma REST surface;
 * live up/down, 24h uptime and latency are joined from Prometheus by
 * monitor_name (see `useKumaMetrics`) — best-effort, so the table still renders
 * if Prometheus is unavailable (those columns fall back to "—").
 */
export function KumaUptimePage() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const base = `/services/healthcheck/${projectId}`;
  const { show } = useToast();

  const { data, isLoading, isError, refetch } = useKumaMonitors();
  const monitors = data ?? [];

  // Live health joined by monitor_name — best-effort; absent → columns show "—".
  const { data: metricsData } = useKumaMetrics();

  // Quick Stats + recent state-change events (dashboard parity).
  const { data: stats } = useMonitorStats();
  const { data: events = [] } = useMonitorEvents(50);

  const eventColumns = useMemo<ColumnDef<MonitorEvent, unknown>[]>(
    () => [
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const s = row.original.status;
          const tone: StatusTone = s === 1 ? 'success' : s === 0 ? 'danger' : s === 3 ? 'warning' : 'muted';
          const label = s === 1 ? 'Up' : s === 0 ? 'Down' : s === 3 ? 'Maintenance' : 'Pending';
          return <StatusPill tone={tone}>{label}</StatusPill>;
        },
      },
      { id: 'name', header: 'Name', accessorKey: 'name', enableSorting: false },
      { id: 'time', header: 'DateTime', accessorKey: 'time', enableSorting: false },
      { id: 'msg', header: 'Message', accessorKey: 'msg', enableSorting: false },
    ],
    [],
  );

  const [query, setQuery] = useState('');
  const [pendingDelete, setPendingDelete] = useState<KumaMonitor | null>(null);
  const [visible, setVisible] = useState<Record<string, boolean>>(DEFAULT_VISIBLE);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const goCreate = () => navigate(`${base}/kuma-uptime/create`);

  const del = useDeleteKumaMonitor();

  // Active scope derived from the FilterBuilder conditions (multiselect values
  // are comma-joined per row, so split + flatten across every matching row).
  const collect = (field: string) =>
    conditions
      .filter((c) => c.field === field)
      .flatMap((c) => (c.value || '').split(','))
      .map((s) => s.trim())
      .filter(Boolean);
  const statusFilters = useMemo(() => new Set(collect('status')), [conditions]);
  const typeFilters = useMemo(() => new Set(collect('type')), [conditions]);

  // Type options come from the data itself (labelled via the @mgmt schema).
  const filterFields: FilterField[] = useMemo(() => {
    const types = Array.from(new Set(monitors.map((m) => m.type))).sort();
    return [
      {
        value: 'status',
        label: 'Status',
        input: 'multiselect',
        operators: [{ value: 'is_any_of', label: 'is any of' }],
        options: Object.entries(STATUS_LABEL).map(([v, l]) => ({ value: v, label: l })),
      },
      {
        value: 'type',
        label: 'Type',
        input: 'multiselect',
        operators: [{ value: 'is_any_of', label: 'is any of' }],
        options: types.map((t) => ({ value: t, label: TYPE_LABEL[t] ?? t })),
      },
    ];
  }, [monitors]);

  // Search (name/url) + Status/Type filters are all client-side: ListMonitors is
  // a raw Kuma passthrough with no server-side filtering.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return monitors.filter((m) => {
      if (q && !m.name.toLowerCase().includes(q) && !m.url.toLowerCase().includes(q)) return false;
      if (typeFilters.size && !typeFilters.has(m.type)) return false;
      if (statusFilters.size && !statusFilters.has(monitorStatusKey(m, metricsData?.[m.name]?.up))) return false;
      return true;
    });
  }, [monitors, query, typeFilters, statusFilters, metricsData]);

  const confirmDelete = () => {
    if (!pendingDelete) return;
    const m = pendingDelete;
    setPendingDelete(null);
    del.mutate(m.id, {
      onSuccess: () => show({ tone: 'success', title: `Deleted "${m.name}"` }),
      onError: (e) => show({ tone: 'danger', title: e?.message || 'Delete failed' }),
    });
  };

  const columns = useMemo<ColumnDef<KumaMonitor, unknown>[]>(() => {
    const all: Record<string, ColumnDef<KumaMonitor, unknown>> = {
      url: {
        id: 'url',
        header: 'URL',
        enableSorting: false,
        cell: ({ row }) =>
          muted(
            <span
              style={{
                fontFamily: 'var(--vn-font-mono)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'inline-block',
                maxWidth: 320,
              }}
            >
              {row.original.url}
            </span>,
          ),
      },
      type: {
        id: 'type',
        header: 'Type',
        enableSorting: false,
        cell: ({ row }) => (
          <Badge tone="neutral" appearance="subtle" size="sm" style={{ textTransform: 'uppercase' }}>
            {row.original.type}
          </Badge>
        ),
      },
      interval: {
        id: 'interval',
        header: 'Interval',
        enableSorting: false,
        cell: ({ row }) => muted(`${row.original.interval}s`),
      },
      status: {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        // Live up/down from Prometheus (monitor_status), joined by name. Paused
        // monitors have no series; active-but-not-yet-scraped falls back to a
        // neutral "Active" until the first sample lands.
        cell: ({ row }) => {
          const m = metricsData?.[row.original.name];
          if (!row.original.active) return <StatusPill tone="muted">Paused</StatusPill>;
          if (m?.up === true) return <StatusPill tone="success">Up</StatusPill>;
          if (m?.up === false) return <StatusPill tone="danger">Down</StatusPill>;
          return <StatusPill tone="muted">Active</StatusPill>;
        },
      },
      uptime: {
        id: 'uptime',
        header: 'Uptime 24h',
        enableSorting: false,
        cell: ({ row }) => {
          const u = metricsData?.[row.original.name]?.uptime24h;
          return u == null ? muted('—') : muted(`${u.toFixed(2)}%`);
        },
      },
      latency: {
        id: 'latency',
        header: 'Latency',
        enableSorting: false,
        cell: ({ row }) => {
          const ms = metricsData?.[row.original.name]?.responseMs;
          return ms == null ? muted('—') : muted(`${ms} ms`);
        },
      },
    };
    const nameCol: ColumnDef<KumaMonitor, unknown> = {
      id: 'name',
      header: 'Name',
      accessorKey: 'name',
      cell: ({ row }) => (
        <span
          style={{
            fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'],
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'inline-block',
            maxWidth: 240,
          }}
        >
          {row.original.name}
        </span>
      ),
    };
    const actionsCol: ColumnDef<KumaMonitor, unknown> = {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <RowMenu items={[{ label: 'Delete', danger: true, onSelect: () => setPendingDelete(row.original) }]} />
      ),
    };
    return [nameCol, ...TOGGLEABLE.filter((c) => visible[c.id]).map((c) => all[c.id]), actionsCol];
  }, [metricsData, visible]);

  return (
    <Container size="xl">
      <PageHeader
        icon={<Gauge size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild><Link to={base}>Monitoring</Link></BreadcrumbItem>
            <BreadcrumbItem current>Uptime</BreadcrumbItem>
          </Breadcrumbs>
        }
        title="Uptime"
        description="Uptime monitors backed by Uptime Kuma."
        actions={
          <Button size="sm" startIcon={<Plus size={15} />} onClick={goCreate}>
            Add monitor
          </Button>
        }
      />

      {stats && (
        <StatGroup columns={5} style={{ marginBottom: 'var(--vn-space-4)' }}>
          <Stat label="Up" value={stats.up} />
          <Stat label="Down" value={stats.down} />
          <Stat label="Maintenance" value={stats.maintenance} />
          <Stat label="Pending" value={stats.pending} />
          <Stat label="Paused" value={stats.pause} />
        </StatGroup>
      )}

      <Toolbar
        start={
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search monitors"
            startSlot={<Search size={15} />}
            style={{ minWidth: 260 }}
          />
        }
        end={
          <>
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverTrigger asChild>
                <Button variant="secondary" size="sm" startIcon={<Filter size={15} />}>
                  Filters{conditions.length ? ` (${conditions.length})` : ''}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" style={{ padding: 0 }}>
                <FilterBuilder
                  fields={filterFields}
                  defaultValue={conditions.length ? conditions : undefined}
                  onApply={(c) => {
                    // Drop empty rows so a blank filter doesn't count as active.
                    setConditions(c.filter((x) => x.value.trim()));
                    setFilterOpen(false);
                  }}
                  onClose={() => setFilterOpen(false)}
                />
              </PopoverContent>
            </Popover>
            {conditions.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setConditions([])}>
                Clear
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="sm" startIcon={<SlidersHorizontal size={15} />}>
                  Display options
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                {TOGGLEABLE.map((c) => (
                  <DropdownMenuCheckboxItem
                    key={c.id}
                    checked={visible[c.id]}
                    onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.id]: v === true }))}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {c.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="secondary"
              size="sm"
              startIcon={<Download size={15} />}
              disabled={filtered.length === 0}
              onClick={() => exportCsv(filtered, metricsData)}
            >
              Export
            </Button>
          </>
        }
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Couldn't load monitors"
          actions={
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          The monitoring service is temporarily unavailable. Please try again.
        </Alert>
      ) : isLoading ? (
        <Skeleton style={{ height: 280 }} />
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          getRowId={(m) => String(m.id)}
          // Row opens the monitor's detail; the kebab's RowMenu stops propagation
          // so Delete still works without triggering navigation.
          onRowClick={(m) => navigate(`${base}/kuma-uptime/${encodeURIComponent(m.name)}`)}
          emptyState={
            // No monitors at all → first-use CTA; a search/filter with no hit is
            // just a miss, so a plain string keeps the frame + headers.
            query.trim() || conditions.length ? (
              'No monitors match your search or filters.'
            ) : (
              <EmptyState
                icon={<Gauge size={28} />}
                title="No monitors"
                description="Add your first Uptime-Kuma monitor to start tracking endpoints."
                actions={
                  <Button size="sm" startIcon={<Plus size={15} />} onClick={goCreate}>
                    Add monitor
                  </Button>
                }
              />
            )
          }
          footer={filtered.length ? `Showing ${filtered.length} monitor${filtered.length === 1 ? '' : 's'}` : undefined}
        />
      )}

      {events.length > 0 && (
        <div style={{ marginTop: 'var(--vn-space-5)' }}>
          <h2 style={{ fontSize: 'var(--vn-font-size-md)', fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'], marginBottom: 'var(--vn-space-3)' }}>
            Recent Events
          </h2>
          <DataTable
            columns={eventColumns}
            data={events}
            getRowId={(e, i) => `${e.monitor_id}-${e.time}-${i}`}
          />
        </div>
      )}
      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        destructive
        title="Delete monitor?"
        description={pendingDelete ? `"${pendingDelete.name}" will be removed from Uptime Kuma. This cannot be undone.` : ''}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
      />
    </Container>
  );
}

